import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthService, { setLogoutFunction } from '../services/auth-service';
import { jwtDecode } from 'jwt-decode';
import LocalStorage from '../helper/storage';
import { SignupCredentials } from 'types';
import eventBus from '../events/event-bus';
import { StoreEventTracker } from '../helper/analytics-tracker';

interface DecodedToken {
  exp: number; // expiration time as a Unix timestamp
}

// Define the shape of a user object.
export interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  status: string;
  role: UserRole;
  token: string;
  store: Store;
  logisticCompany: LogisticCompany;
}

export interface Store {
  id: number;
  name: string;
  address: string;
  phone: string;
  status: string;
  logisticCompany: LogisticCompany;
}

export interface LogisticCompany {
  id: number;
  name: string;
  address: string;
  phone: string;
  status: string;
}

export enum UserRole {
  ADMIN = "ADMIN",
  STORE_ATTENDANT = "STORE_ATTENDANT",
  LOGISTICS_MANAGER = "LOGISTICS_MANAGER",
  CUSTOMER = "CUSTOMER"
}



// Define the context type, including the user and authentication actions.
interface AuthContextType {
  user: User | null;
  isAuth: boolean;
  errorMessage: string;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  register: (payload: SignupCredentials) => Promise<boolean>;
}

// Create the AuthContext with an initial undefined value.
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Define the props for the provider.
interface AuthProviderProps {
  children: ReactNode;
}

// AuthProvider component that wraps your app and makes the auth object available.
export const AuthProvider = ({ children }: AuthProviderProps) => {
  // Initialize state from localStorage.
  // const storedUser = useMemo(async () => await LocalStorage.getItem<User | null>('user') as User, []);
  const [user, setUser] = useState<User | null>(null);
  const [isAuth, setIsAuth] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const navigate = useNavigate();

  const isTokenValid = (token: string): boolean => {
    try {
      const decoded = jwtDecode<DecodedToken>(token); // Decode the token
  
      // Get the current time in seconds (JWT exp is also in seconds)
      const currentTime = Date.now() / 1000;
  
      // Check if the token is expired
      if (decoded.exp < currentTime) {
        return false;
      }
  
      return true;

    } catch (error) {

      return false; 
      
    }
  };


  // A logout function that resets the user state.
  const logout = useCallback(() => {

    setUser(null);
    
    setIsAuth(false);
    
    LocalStorage.removeItem('user');
    
    navigate("/login", { replace: true });

    return ;

  }, [navigate]);

  // A simple login function that sets the user state.
  const authenticate = useCallback((user: User | null) => {

    if (!user) {
      logout();
      return false;
    }

    if (!isTokenValid(user.token)) {
      logout();
      return false;
    }

    setUser(user);

    setIsAuth(true);
    
    LocalStorage.saveItem<User>('user', user);

    return true;

  }, [logout, isTokenValid]);


  useEffect(() => {
    const fetchUser = async () => {
      
      try {
        
        const storedUser = await LocalStorage.getItem<User | null>('user');
  
        if (!storedUser) {
          return ;
        }
        
        authenticate(storedUser);

      } catch (error) {
        console.error('Error parsing user from localStorage:', error);
      }

    };

    fetchUser();
  }, []);


  useEffect(() => {

    if (!user?.id) {
      return ;
    }

    let updatedUser = user;

    const handleKycEventUpdate = (e: Event) => {
      const event = e as CustomEvent;
      const eventUser = event.detail?.user;

      if (!eventUser) {
        return ;
      }
      
      if (eventUser?.customer?.lastName === updatedUser.lastName) {
        return ;
      }

      const eventUserUpdated = { ...eventUser?.customer, id: eventUser.id };

      updatedUser = { ...updatedUser, ...eventUserUpdated };

      authenticate(updatedUser);
    }

    eventBus.addEventListener('kyc:updated', handleKycEventUpdate);

    return () => eventBus.removeEventListener('kyc:updated', handleKycEventUpdate);

  }, [user?.id]);

  const login = async (email: string, password: string) => {
    try {

      const { data, status, message } = await AuthService.login({email, password});

      if (status !== 200) {
        setErrorMessage(message);
        return false;
      } 
      
      const isAuthenticated = authenticate(data);
      
      // Track successful login
      if (isAuthenticated) {
        StoreEventTracker.trackUserLogin('email');
      }
      
      return isAuthenticated;
      
    } catch (error) {
      console.log("error = ", error)
      return false;
    }
  }
  
  const register = async (payload: SignupCredentials) => {
    try {

      const { data, status, message } = await AuthService.register(payload);

      if (status !== 201) {
        setErrorMessage(message);
        return false;
      } 
      
      const isAuthenticated = authenticate(data);
      
      // Track successful registration
      if (isAuthenticated) {
        StoreEventTracker.trackUserRegistration('email');
      }
      
      return isAuthenticated;
      
    } catch (error) {
      console.log("error = ", error)
      return false;
    }
  }


  useEffect(() => {
    setLogoutFunction(logout);
  }, [logout]);

  return (
    <AuthContext.Provider value={{ errorMessage, user, isAuth, login, logout, register }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for consuming the auth context.
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
